import { useState } from 'react';
import { Button, Col, Form, Input, Modal, Row, Select, TimePicker } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiCreateProviderDataSponsorScheduler } from '../../api';
import { CreateProviderDataSponsorSchedulerRequest } from '../../interfaces/provider-data-sponsor-scheduler.interface';
import { systemProviderDataSponsorOptions } from '../../assets/constants/default_option';
import { FORMAT_TIME } from '../../utils/entity-utils';
import { toastError, toastSuccess } from '../../utils/toast';
import { useLoading } from '../../context/LoadingContext';

interface ProviderDataSponsorSchedulerItemForm {
    time: any;
    provider: string;
}

interface ProviderDataSponsorSchedulerFormValues {
    items: ProviderDataSponsorSchedulerItemForm[];
}

interface ModalCreateProviderDataSponsorSchedulerProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const providerOptions = systemProviderDataSponsorOptions.filter(item => item.value !== '');

export default function ModalCreateProviderDataSponsorScheduler({ open, onClose, onSuccess }: ModalCreateProviderDataSponsorSchedulerProps) {
    const [form] = Form.useForm<ProviderDataSponsorSchedulerFormValues>();
    const [otpValue, setOtpValue] = useState('');
    const { setIsLoading } = useLoading();

    const handleCancel = () => {
        form.resetFields();
        setOtpValue('');
        onClose();
    };

    const handleCreate = async () => {
        try {
            if (!otpValue) {
                return toastError('Vui lòng nhập mã OTP xác nhận');
            }
            await form.validateFields();
            const values = form.getFieldsValue();
            const times = values.items.map(item => dayjs(item.time).format(FORMAT_TIME));
            const uniqueTimes = new Set(times);
            if (uniqueTimes.size !== times.length) {
                return toastError('Không được chọn trùng thời gian');
            }
            const payload: CreateProviderDataSponsorSchedulerRequest = {
                items: values.items.map(item => ({
                    time: dayjs(item.time).format(FORMAT_TIME),
                    provider: item.provider,
                })),
                otpConfirm: otpValue,
            };
            setIsLoading(true);
            const resp = await apiCreateProviderDataSponsorScheduler(payload);
            if (resp.status === 200) {
                toastSuccess('Đã tạo lịch đổi nhà cung cấp đại trà');
                form.resetFields();
                setOtpValue('');
                onSuccess();
                onClose();
            }
        } catch (error: any) {
            if (error.errorFields) {
                const firstErr = error.errorFields?.[0]?.errors?.[0];
                toastError(firstErr || 'Vui lòng nhập đầy đủ thông tin');
            } else {
                toastError('Có lỗi xảy ra');
            }
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            title="Tạo lịch đổi nhà cung cấp đại trà"
            open={open}
            onCancel={handleCancel}
            width={720}
            centered
            maskClosable={false}
            footer={[
                <Button key="cancel" onClick={handleCancel}>Hủy</Button>,
                <Button key="submit" type="primary" onClick={handleCreate}>Tạo lịch</Button>,
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ items: [{ time: null, provider: 'data_sponsor' }] }}
            >
                <Form.List
                    name="items"
                    rules={[
                        {
                            validator: async (_, items) => {
                                if (!items || items.length < 1) {
                                    return Promise.reject(new Error('Vui lòng thêm ít nhất một mốc giờ'));
                                }
                                const formattedTimes = items
                                    .filter((item: ProviderDataSponsorSchedulerItemForm) => item?.time)
                                    .map((item: ProviderDataSponsorSchedulerItemForm) => dayjs(item.time).format(FORMAT_TIME));
                                if (new Set(formattedTimes).size !== formattedTimes.length) {
                                    return Promise.reject(new Error('Không được chọn trùng thời gian'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    {(fields, { add, remove }, { errors }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Row key={key} gutter={16} align="middle">
                                    <Col span={10}>
                                        <Form.Item
                                            {...restField}
                                            label="Thời gian"
                                            name={[name, 'time']}
                                            rules={[{ required: true, message: 'Chọn thời gian' }]}
                                        >
                                            <TimePicker
                                                style={{ width: '100%' }}
                                                format={FORMAT_TIME}
                                                placeholder="Thời gian thực hiện"
                                                onSelect={value => {
                                                    const current = form.getFieldValue(['items', name, 'time']);
                                                    form.setFieldValue(
                                                        ['items', name, 'time'],
                                                        current ? value : value.minute(0).second(0)
                                                    );
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={10}>
                                        <Form.Item
                                            {...restField}
                                            label="Nhà cung cấp"
                                            name={[name, 'provider']}
                                            rules={[{ required: true, message: 'Chọn nhà cung cấp' }]}
                                        >
                                            <Select options={providerOptions} placeholder="Chọn nhà cung cấp" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} />}
                                    </Col>
                                </Row>
                            ))}
                            <Form.ErrorList errors={errors} />
                            <Form.Item>
                                <Button type="dashed" onClick={() => add({ provider: 'data_sponsor' })} block icon={<PlusOutlined />}>
                                    Thêm mốc giờ
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>
                <Form.Item label="Mã OTP xác nhận" required>
                    <Input value={otpValue} onChange={e => setOtpValue(e.target.value)} />
                </Form.Item>
            </Form>
        </Modal>
    );
}
